import React, { useEffect, useState } from "react";
import { PlusIcon, PencilIcon, TrashIcon, EyeIcon, ArrowUturnLeftIcon, AcademicCapIcon, CheckCircleIcon, PauseCircleIcon } from "@heroicons/react/24/outline";
import { Link, useNavigate } from "react-router-dom";
import CourseContentManagerModal from "../components/CourseContentManagerModal";
import { useAdminLayout } from "../contexts/AdminLayoutContext";
import { useConfirm } from "../contexts/ConfirmContext";
import { toast } from "react-toastify";
import MoreInfoModal from "../components/MoreInfoModal";
import CreatableSelect from "react-select/creatable";

// Utility function to strip HTML tags from a string
function stripHtmlTags(str) {
  if (!str) return "";
  const txt = document.createElement("textarea");
  txt.innerHTML = str;
  const decoded = txt.value;
  return decoded.replace(/<[^>]*>/g, "");
}

// Format categories for display (API may return string or array)
function formatCategories(categories) {
  if (!categories) return "—";
  if (Array.isArray(categories)) return categories.join(", ");
  return typeof categories === "string" ? categories : "—";
}

const Courses = () => {
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [newlyCreatedCourseId, setNewlyCreatedCourseId] = useState(null);
  const [previewCourse, setPreviewCourse] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const { showAddCourseModal, openAddCourseModal, closeAddCourseModal } =
    useAdminLayout();
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    level: "beginner",
    duration: "",
    price: "",
    status: "draft",
  });

  const navigate = useNavigate();
  const { confirm } = useConfirm();

  // Add state for workflow
  const [workflowStep, setWorkflowStep] = useState(1); // 1: Basic, 2: More Info, 3: Lessons, 4: Content
  const [currentCourseId, setCurrentCourseId] = useState(null);

  // Add state for instructors
  const [instructors, setInstructors] = useState([]);
  const [selectedInstructorId, setSelectedInstructorId] = useState("");

  // Add state for preview modal
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [editingPreview, setEditingPreview] = useState(false);
  const [previewForm, setPreviewForm] = useState(null);
  const [previewMoreInfo, setPreviewMoreInfo] = useState(null);
  const [previewViewOnly, setPreviewViewOnly] = useState(false);

  // Add More Info Modal
  const [showMoreInfoModal, setShowMoreInfoModal] = useState(false);

  // Add Content Modal
  const [showContentManager, setShowContentManager] = useState(false);

  const [courseSearchQuery, setCourseSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const COURSES_PER_PAGE = 10;
  const [showTrashModal, setShowTrashModal] = useState(false);
  const [trashList, setTrashList] = useState([]);
  const [trashLoading, setTrashLoading] = useState(false);

  // Helper to get JWT token from localStorage
  const getAuthHeaders = () => {
    const token = localStorage.getItem("token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const API_URL =
    process.env.REACT_APP_API_URL || "https://api.isyllabi.com/api";
  const ISYLLABI_BASE_URL =
    process.env.REACT_APP_ISYLLABI_URL || "http://localhost:3000";
  const createSlug = (title) =>
    (title || "")
      .toLowerCase()
      .replace(/\s+/g, "-")
      .replace(/[^\w-]/g, "");

  // Helper to resolve absolute image URLs from API responses (same pattern as iSyllabi: /api/files/...)
  const resolveImageUrl = (url) => {
    if (url == null || (typeof url === "string" && !url.trim())) return null;
    const str = typeof url === "string" ? url.trim() : String(url);
    const isAbsolute = /^(?:[a-z]+:)?\/\//i.test(str);
    if (isAbsolute) return str;
    // Build path like iSyllabi getCourseImage: /files/... then append to API base
    let path = str;
    if (path.startsWith("/storage/")) {
      path = "/files/" + path.replace(/^\/?storage\/?/, "");
    } else if (path.startsWith("storage/")) {
      path = "/files/" + path.replace(/^storage\/?/, "");
    } else if (!path.startsWith("/files/") && !path.startsWith("files/")) {
      path = path.startsWith("/") ? "/files" + path : "/files/" + path;
    } else if (path.startsWith("files/")) {
      path = "/" + path;
    }
    const normalized = path.startsWith("/") ? path : `/${path}`;
    let base = (API_URL || "").replace(/\/$/, "");
    // Production serves files at /api/files/* — ensure base includes /api so we don't request /files/* (404)
    if (base && !base.endsWith("/api")) base = base + "/api";
    return base ? `${base}${normalized}` : normalized;
  };

  useEffect(() => {
    fetchCourses();
    fetchInstructors();
    fetchTrash();
  }, []);

  const fetchCourses = async () => {
    try {
      setLoading(true);
      console.log("Fetching courses...");

      const response = await fetch(`${API_URL}/courses`, {
        headers: {
          Accept: "application/json",
          ...getAuthHeaders(),
        },
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error("Error response:", errorText.substring(0, 500));
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log(`Successfully loaded ${data.length} courses`);

      // Process the courses (include both image and thumbnail so either API shape works)
      const processedCourses = data.map((course) => ({
        id: course.id,
        title: course.title,
        status: course.status,
        created_at: course.created_at,
        updated_at: course.updated_at,
        thumbnail: course.thumbnail ?? course.image,
        image: course.image ?? course.thumbnail,
        instructor: course.instructor,
        categories: course.categories,
        level: course.level,
        duration: course.duration,
        price: course.price != null ? course.price : null,
        // Ensure lessons count is carried over from API
        lessons_count:
          typeof course.lessons_count === "number"
            ? course.lessons_count
            : typeof course.lessonsCount === "number"
            ? course.lessonsCount
            : 0,
        // Add a method to fetch full course details when needed
        fetchFullDetails: async () => {
          const res = await fetch(`${API_URL}/courses/${course.id}`, {
            headers: getAuthHeaders(),
          });
          if (!res.ok) throw new Error("Failed to fetch course details");
          return await res.json();
        },
      }));

      setCourses(processedCourses);
      setLoading(false);
    } catch (err) {
      console.error("Error in fetchCourses:", err);
      setError(`Failed to fetch courses: ${err.message}`);
      setLoading(false);
      toast.error(`Failed to fetch courses: ${err.message}`);
    }
  };

  const fetchInstructors = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API_URL}/users`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      if (Array.isArray(data)) {
        const instructorsOnly = data.filter((u) => u.role === "instructor");
        setInstructors(instructorsOnly);
        if (instructorsOnly.length > 0) {
          setSelectedInstructorId(instructorsOnly[0].id);
        }
      }
    } catch (err) {
      // ignore
    }
  };

  const handleInputChange = (e) => {
    const { name, value, files } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: files ? files[0] : value,
    }));
  };

  const handleSubmitAddCourse = async (e) => {
    e.preventDefault();
    try {
      if (!formData.title || !formData.description || !formData.duration) {
        toast.error("Please fill in all required fields");
        return;
      }
      if (!selectedInstructorId) {
        toast.error("Please select an instructor");
        return;
      }
      // Handle thumbnail upload if present (TODO: implement if needed)
      const response = await fetch(`${API_URL}/courses`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...getAuthHeaders(),
        },
        body: JSON.stringify({
          ...formData,
          instructor_id: selectedInstructorId,
        }),
      });
      const data = await response.json();
      if (!response.ok)
        throw new Error(data.message || "Failed to save course");
      setCurrentCourseId(data.id);
      setWorkflowStep(2); // Move to More Info step
      closeAddCourseModal();
      fetchCourses();
      toast.success("Course created successfully");
    } catch (err) {
      toast.error(err.message || "Failed to save course");
    }
  };

  const handleDelete = async (courseId) => {
    const ok = await confirm({
      title: "Delete course",
      message: "Are you sure you want to delete this course? It will be moved to the Delete Bin.",
      confirmLabel: "Delete",
      cancelLabel: "Cancel",
      variant: "danger",
    });
    if (!ok) return;
    try {
      const response = await fetch(`${API_URL}/courses/${courseId}`, {
        method: "DELETE",
        headers: { ...getAuthHeaders() },
      });
      const data = await response.json();
      if (!response.ok)
        throw new Error(data.message || data.error || "Failed to delete course");
      fetchCourses();
      fetchTrash();
      toast.success("Course deleted successfully");
    } catch (err) {
      console.error("Delete error:", err);
      toast.error(err.message || "Failed to delete course");
    }
  };

  const handleStatusChange = async (course, newStatus) => {
    const courseId = course.id;
    if (newStatus === "published") {
      // Validation: price must not be negative (null/empty/0 = free course is OK); require at least one lesson
      const priceNum = course.price != null && course.price !== "" ? Number(course.price) : 0;
      const hasValidPrice = !Number.isNaN(priceNum) && priceNum >= 0;
      const lessonsCount = typeof course.lessons_count === "number" ? course.lessons_count : 0;
      const hasAtLeastOneLesson = lessonsCount >= 1;
      if (!hasValidPrice) {
        toast.error("Cannot publish: Course price cannot be negative (Edit course → Pricing).");
        return;
      }
      if (!hasAtLeastOneLesson) {
        toast.error("Cannot publish: Add at least one class/lesson to the course first (Edit course → Classes).");
        return;
      }
      const confirmed = await confirm({
        title: "Publish course",
        message: "Are you sure you want to publish this course? Once published, it will be visible to all students.",
        confirmLabel: "Publish",
        cancelLabel: "Cancel",
        variant: "info",
      });
      if (!confirmed) return;
    }
    try {
      const response = await fetch(`${API_URL}/courses/${courseId}/status`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          ...getAuthHeaders(),
        },
        body: JSON.stringify({ status: newStatus }),
      });
      const data = await response.json();
      if (!response.ok)
        throw new Error(data.message || data.error || "Failed to update course status");
      fetchCourses();
      toast.success(`Course ${newStatus} successfully`);
    } catch (err) {
      toast.error(err.message || "Failed to update course status");
    }
  };

  const fetchTrash = async () => {
    try {
      setTrashLoading(true);
      const response = await fetch(`${API_URL}/courses/trash`, {
        headers: getAuthHeaders(),
      });
      if (!response.ok) throw new Error("Failed to fetch trash");
      const data = await response.json();
      setTrashList(Array.isArray(data) ? data : []);
    } catch (err) {
      setTrashList([]);
      toast.error("Failed to load deleted courses");
    } finally {
      setTrashLoading(false);
    }
  };

  const openTrashModal = () => {
    setShowTrashModal(true);
    fetchTrash();
  };

  const handleRestore = async (courseId) => {
    try {
      const response = await fetch(`${API_URL}/courses/${courseId}/restore`, {
        method: "PATCH",
        headers: getAuthHeaders(),
      });
      if (!response.ok) throw new Error("Failed to restore");
      fetchTrash();
      fetchCourses();
      toast.success("Course restored");
    } catch (err) {
      toast.error("Failed to restore course");
    }
  };

  const handlePermanentDelete = async (courseId) => {
    const ok = await confirm({
      title: "Permanent delete",
      message: "Permanently delete this course? This cannot be undone.",
      confirmLabel: "Delete permanently",
      cancelLabel: "Cancel",
      variant: "danger",
    });
    if (!ok) return;
    try {
      const response = await fetch(`${API_URL}/courses/${courseId}/permanent`, {
        method: "DELETE",
        headers: getAuthHeaders(),
      });
      if (!response.ok) throw new Error("Failed to delete");
      fetchTrash();
      fetchCourses();
      toast.success("Course permanently deleted");
    } catch (err) {
      toast.error("Failed to delete course");
    }
  };

  const handlePreview = async (course, moreInfoFields = null) => {
    try {
      // If we already have the full course data, use it
      if (course.lessons && course.instructor) {
        const merged = moreInfoFields
          ? { ...course, ...moreInfoFields }
          : course;
        // Normalize fields for preview consistency
        const normalized = {
          ...merged,
          image:
            merged.image || merged.thumbnail || "/images/default-course.jpg",
          categories: Array.isArray(merged.categories)
            ? merged.categories
            : (merged.categories || "")
                .split(",")
                .map((cat) => cat.trim())
                .filter(Boolean),
          tags: Array.isArray(merged.tags)
            ? merged.tags
            : (merged.tags || "")
                .split(",")
                .map((tag) => tag.trim())
                .filter(Boolean),
        };
        // Force resolved absolute URL for preview image
        const resolved = resolveImageUrl(
          normalized.image || normalized.thumbnail
        );
        if (resolved) {
          normalized.image = resolved;
          normalized.thumbnail = resolved;
        }
        console.log("[Preview] Using image URL:", normalized.image);
        setPreviewCourse(normalized);
        setPreviewForm({
          title: normalized.title,
          description: normalized.description,
          // Add other fields as needed
        });
        setShowPreviewModal(true);
        return;
      }

      // Otherwise, fetch the full course data
      const response = await fetch(`${API_URL}/courses/${course.id}`, {
        headers: {
          ...getAuthHeaders(),
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to fetch course preview");
      }

      const data = await response.json();

      // Format the data for preview
      const formattedData = {
        ...data,
        // Ensure image URL is properly formatted - use the thumbnail accessor from backend
        image: data.thumbnail || data.image || "/images/default-course.jpg",
        thumbnail: data.thumbnail || data.image || "/images/default-course.jpg",
        // Ensure categories and tags are arrays
        categories: Array.isArray(data.categories)
          ? data.categories
          : (data.categories || "")
              .split(",")
              .map((cat) => cat.trim())
              .filter(Boolean),
        tags: Array.isArray(data.tags)
          ? data.tags
          : (data.tags || "")
              .split(",")
              .map((tag) => tag.trim())
              .filter(Boolean),
      };

      // Merge moreInfoFields if provided (e.g., after adding new course)
      const merged = moreInfoFields
        ? { ...formattedData, ...moreInfoFields }
        : formattedData;

      // Force resolved absolute URL for preview image
      const resolved = resolveImageUrl(merged.image || merged.thumbnail);
      if (resolved) {
        merged.image = resolved;
        merged.thumbnail = resolved;
      }
      console.log("[Preview] Using image URL:", merged.image);
      setPreviewCourse(merged);
      setPreviewForm({
        title: merged.title,
        description: merged.description,
        level: merged.level,
        duration: merged.duration,
        price: merged.price,
        status: merged.status,
        instructorId: merged.instructorId,
      });
      setPreviewMoreInfo({
        prerequisites: merged.prerequisites || "",
        learningObjectives: merged.learningObjectives || "",
        targetAudience: merged.targetAudience || "",
        categories: Array.isArray(merged.categories)
          ? merged.categories.join(", ")
          : merged.categories || "",
        tags: Array.isArray(merged.tags)
          ? merged.tags.join(", ")
          : merged.tags || "",
        thumbnailUrl: merged.thumbnail || null,
      });
      setShowPreviewModal(true);
      setEditingPreview(false);
    } catch (err) {
      toast.error("Failed to fetch course preview");
    }
  };

  const handleEditPreview = () => {
    setEditingPreview(true);
  };

  const handleClosePreviewModal = () => {
    setShowPreviewModal(false);
    setEditingPreview(false);
    setPreviewViewOnly(false);
  };

  const handlePreviewFormChange = (e) => {
    const { name, value } = e.target;
    setPreviewForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSavePreview = async (moreInfoFields) => {
    try {
      console.log(
        "handleSavePreview called with moreInfoFields:",
        moreInfoFields
      );
      // Handle thumbnail upload if it's a new file
      let thumbnailUrl = null;
      if (moreInfoFields.thumbnail instanceof File) {
        const formData = new FormData();
        formData.append("image", moreInfoFields.thumbnail);
        const token = localStorage.getItem("token");
        const uploadRes = await fetch(`${API_URL}/upload/image`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
          },
          body: formData,
        });
        const uploadData = await uploadRes.json();
        if (!uploadRes.ok || !uploadData.url) {
          throw new Error(uploadData.error || "Failed to upload image");
        }
        thumbnailUrl = uploadData.url;

        // Update preview state immediately with new thumbnail
        setPreviewCourse((prev) => ({
          ...prev,
          image: thumbnailUrl,
          thumbnail: thumbnailUrl,
        }));
        setPreviewMoreInfo((prev) => ({
          ...prev,
          thumbnailUrl: thumbnailUrl,
        }));
      } else if (
        typeof moreInfoFields.thumbnail === "string" &&
        moreInfoFields.thumbnail
      ) {
        // Use existing URL
        thumbnailUrl = moreInfoFields.thumbnail;
      }

      // Prepare the request body with proper field mapping
      const body = {
        ...previewForm,
        // Map instructorId to instructor_id for backend compatibility
        instructor_id: previewForm.instructorId,
        // Convert duration to integer if it exists
        duration: previewForm.duration ? parseInt(previewForm.duration) : null,
        // Convert price to number if it exists
        price: previewForm.price ? parseFloat(previewForm.price) : null,
        // Use the correct field names from previewMoreInfo and ensure they are strings
        categories:
          typeof moreInfoFields.categories === "string"
            ? moreInfoFields.categories
            : Array.isArray(moreInfoFields.categories)
            ? moreInfoFields.categories.join(", ")
            : "",
        tags:
          typeof moreInfoFields.tags === "string"
            ? moreInfoFields.tags
            : Array.isArray(moreInfoFields.tags)
            ? moreInfoFields.tags.join(", ")
            : "",
        prerequisites: moreInfoFields.prerequisites || "",
        learning_objectives: moreInfoFields.learningObjectives || "",
        target_audience: moreInfoFields.targetAudience || "",
        thumbnail: thumbnailUrl,
      };

      // Clean up the body to remove any undefined or null values that might cause validation issues
      Object.keys(body).forEach((key) => {
        if (body[key] === undefined || body[key] === null) {
          delete body[key];
        }
      });

      // Remove the camelCase field to avoid confusion
      delete body.instructorId;

      console.log("Sending course update request:", {
        courseId: previewCourse.id,
        requestData: body,
        previewForm: previewForm,
        moreInfoFields: moreInfoFields,
        categoriesType: typeof body.categories,
        categoriesValue: body.categories,
        tagsType: typeof body.tags,
        tagsValue: body.tags,
      });

      const response = await fetch(`${API_URL}/courses/${previewCourse.id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          ...getAuthHeaders(),
        },
        body: JSON.stringify(body),
      });
      const data = await response.json();
      if (!response.ok) {
        console.error("Update failed:", {
          status: response.status,
          statusText: response.statusText,
          data: data,
          requestBody: body,
          requestBodyString: JSON.stringify(body),
        });

        // If it's a validation error, show more details
        if (response.status === 422 && data.errors) {
          console.error("Validation errors:", data.errors);
          const errorMessages = Object.entries(data.errors)
            .map(([field, messages]) => `${field}: ${messages.join(", ")}`)
            .join("; ");
          throw new Error(`Validation failed: ${errorMessages}`);
        }

        throw new Error(
          data.error ||
            data.message ||
            `Failed to update course (${response.status})`
        );
      }
      // Update the course in the list with new thumbnail
      setCourses((prev) =>
        prev.map((course) =>
          course.id === previewCourse.id
            ? { ...course, image: thumbnailUrl, thumbnail: thumbnailUrl }
            : course
        )
      );

      setShowPreviewModal(false);
      setEditingPreview(false);
      fetchCourses();
      toast.success("Course updated successfully");
    } catch (err) {
      console.error("Save preview error:", err);
      toast.error(err.message || "Failed to update course");
    }
  };

  // Filter courses based on search query (table uses courseSearchQuery)
  const filteredCourses = courses.filter(
    (course) =>
      course.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      course.description?.toLowerCase().includes(searchQuery.toLowerCase())
  );
  const searchFilteredCourses = filteredCourses.filter(
    (course) =>
      course.title.toLowerCase().includes(courseSearchQuery.toLowerCase()) ||
      course.description?.toLowerCase().includes(courseSearchQuery.toLowerCase())
  );
  const totalPages = Math.max(1, Math.ceil(searchFilteredCourses.length / COURSES_PER_PAGE));
  const paginatedCourses = searchFilteredCourses.slice(
    (currentPage - 1) * COURSES_PER_PAGE,
    currentPage * COURSES_PER_PAGE
  );

  // Reset to page 1 when search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [courseSearchQuery]);

  // Clamp current page when total pages shrinks (e.g. after delete or filter)
  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(totalPages);
  }, [totalPages, currentPage]);

  // Add More Info Modal
  useEffect(() => {
    if (workflowStep === 2 && currentCourseId) {
      setShowMoreInfoModal(true);
    }
  }, [workflowStep, currentCourseId]);

  // When adding a new course and showing preview, pass the more info fields
  const handleSubmitMoreInfo = async (moreInfoData) => {
    try {
      let thumbnailUrl = null;
      if (moreInfoData.thumbnail instanceof File) {
        // Upload new image
        const formData = new FormData();
        formData.append("image", moreInfoData.thumbnail);
        const token = localStorage.getItem("token");
        const uploadRes = await fetch(`${API_URL}/upload/image`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
          },
          body: formData,
        });
        const uploadData = await uploadRes.json();
        if (!uploadRes.ok || !uploadData.url) {
          throw new Error(uploadData.error || "Failed to upload image");
        }
        // Use full URL for database storage (like notes do)
        thumbnailUrl = uploadData.url;
        // Set previewMoreInfo.thumbnailUrl to the backend file URL
        setPreviewMoreInfo((prev) => ({ ...prev, thumbnailUrl }));
      } else if (
        typeof moreInfoData.thumbnail === "string" &&
        moreInfoData.thumbnail
      ) {
        // Use existing URL
        thumbnailUrl = moreInfoData.thumbnail;
      } else {
        // No image
        thumbnailUrl = null;
      }

      // Now update the course with the thumbnail URL (or null if not set)
      const requestData = { ...moreInfoData, thumbnail: thumbnailUrl };
      console.log("Sending course update request:", {
        courseId: currentCourseId,
        requestData: requestData,
        moreInfoData: moreInfoData,
        thumbnailUrl: thumbnailUrl,
      });

      const response = await fetch(`${API_URL}/courses/${currentCourseId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          ...getAuthHeaders(),
        },
        body: JSON.stringify(requestData),
      });
      const data = await response.json();
      if (!response.ok)
        throw new Error(data.message || "Failed to update course");
      setShowMoreInfoModal(false);
      setEditingPreview(false);
      setPreviewViewOnly(false);
      fetchCourses();
      toast.success("Course updated successfully");
      // Show preview with the latest more info fields
      handlePreview(data, { ...moreInfoData, thumbnail: thumbnailUrl });
    } catch (err) {
      toast.error(err.message || "Failed to update course");
    }
  };

  // Add Course Modal
  const renderAddCourseModal = () => {
    if (!showAddCourseModal) return null;

    return (
      <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-lg p-8 w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-2xl border border-gray-200">
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-2xl font-bold text-indigo-700 tracking-tight">
              Add New Course
            </h3>
            <button
              onClick={closeAddCourseModal}
              className="text-gray-400 hover:text-gray-600 transition-colors"
              title="Close"
            >
              <span className="sr-only">Close</span>
              <svg
                className="h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
          <form onSubmit={handleSubmitAddCourse} className="space-y-8">
            {/* Basic Info */}
            <div>
              <h4 className="text-lg font-semibold text-gray-800 mb-2">
                Basic Info
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Course Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="title"
                    required
                    placeholder="e.g. Introduction to Python"
                    value={formData.title}
                    onChange={handleInputChange}
                    className="mt-1 block w-full rounded-none border-2 border-black shadow-sm focus:border-indigo-600 focus:ring-1 focus:ring-indigo-600 sm:text-sm placeholder-gray-400 pl-4 py-2 tracking-wide text-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Level
                  </label>
                  <select
                    name="level"
                    value={formData.level}
                    onChange={handleInputChange}
                    className="mt-1 block w-full rounded-none border-2 border-black shadow-sm focus:border-indigo-600 focus:ring-1 focus:ring-indigo-600 sm:text-sm pl-4 py-2 tracking-wide text-lg"
                  >
                    <option value="beginner">Beginner</option>
                    <option value="intermediate">Intermediate</option>
                    <option value="advanced">Advanced</option>
                  </select>
                </div>
              </div>
              {/* Instructor Selection */}
              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700">
                  Instructor <span className="text-red-500">*</span>
                </label>
                <select
                  name="instructorId"
                  value={selectedInstructorId}
                  onChange={(e) => setSelectedInstructorId(e.target.value)}
                  required
                  className="mt-1 block w-full rounded-none border-2 border-black shadow-sm focus:border-indigo-600 focus:ring-1 focus:ring-indigo-600 sm:text-sm pl-4 py-2 tracking-wide text-lg"
                >
                  <option value="">Select Instructor</option>
                  {instructors.map((inst) => (
                    <option key={inst.id} value={inst.id}>
                      {inst.firstName} {inst.lastName} ({inst.email})
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <hr className="my-2 border-gray-200" />
            {/* Pricing & Duration */}
            <div>
              <h4 className="text-lg font-semibold text-gray-800 mb-2">
                Pricing & Duration
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Duration (minutes) <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    name="duration"
                    required
                    min="1"
                    placeholder="e.g. 90"
                    value={formData.duration}
                    onChange={handleInputChange}
                    className="mt-1 block w-full rounded-none border-2 border-black shadow-sm focus:border-indigo-600 focus:ring-1 focus:ring-indigo-600 sm:text-sm placeholder-gray-400 pl-4 py-2 tracking-wide text-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Price
                  </label>
                  <input
                    type="number"
                    name="price"
                    min="0"
                    step="0.01"
                    placeholder="e.g. 49.99"
                    value={formData.price}
                    onChange={handleInputChange}
                    className="mt-1 block w-full rounded-none border-2 border-black shadow-sm focus:border-indigo-600 focus:ring-1 focus:ring-indigo-600 sm:text-sm placeholder-gray-400 pl-4 py-2 tracking-wide text-lg"
                  />
                </div>
              </div>
            </div>
            <hr className="my-2 border-gray-200" />
            {/* Details */}
            <div>
              <h4 className="text-lg font-semibold text-gray-800 mb-2">
                Details
              </h4>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Description <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    name="description"
                    required
                    placeholder="Briefly describe the course..."
                    value={formData.description}
                    onChange={handleInputChange}
                    rows="3"
                    className="mt-1 block w-full rounded-none border-2 border-black shadow-sm focus:border-indigo-600 focus:ring-1 focus:ring-indigo-600 sm:text-sm placeholder-gray-400 pl-4 py-2 tracking-wide text-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Status
                  </label>
                  <select
                    name="status"
                    value={formData.status}
                    onChange={handleInputChange}
                    className="mt-1 block w-full rounded-none border-2 border-black shadow-sm focus:border-indigo-600 focus:ring-1 focus:ring-indigo-600 sm:text-sm pl-4 py-2 tracking-wide text-lg"
                  >
                    <option value="draft">Draft</option>
                    <option value="published">Published</option>
                  </select>
                </div>
              </div>
            </div>
            <div className="flex justify-end space-x-3 mt-8">
              <button
                type="button"
                onClick={closeAddCourseModal}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-6 py-2 text-sm font-semibold text-white bg-indigo-600 border border-transparent rounded-md shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors"
              >
                Create Course
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="h-[calc(100vh-64px)] flex flex-col bg-gray-50 overflow-hidden">
      {/* Full-screen header + table */}
      <div className="flex-1 flex flex-col min-h-0 p-4 md:p-6">
        {/* Stats: Total Courses, Active, Inactive, Delete Bin — on top of course section */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
          <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm flex items-start gap-3">
            <div className="flex-shrink-0 p-2 rounded-lg bg-indigo-100 text-indigo-600">
              <AcademicCapIcon className="h-6 w-6" />
            </div>
            <div>
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Total Courses</p>
              <p className="mt-1 text-2xl font-bold text-gray-900">{searchFilteredCourses.length}</p>
            </div>
          </div>
          <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm flex items-start gap-3">
            <div className="flex-shrink-0 p-2 rounded-lg bg-green-100 text-green-600">
              <CheckCircleIcon className="h-6 w-6" />
            </div>
            <div>
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Active</p>
              <p className="mt-1 text-2xl font-bold text-green-600">
                {searchFilteredCourses.filter((c) => c.status === "published").length}
              </p>
            </div>
          </div>
          <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm flex items-start gap-3">
            <div className="flex-shrink-0 p-2 rounded-lg bg-amber-100 text-amber-600">
              <PauseCircleIcon className="h-6 w-6" />
            </div>
            <div>
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Inactive</p>
              <p className="mt-1 text-2xl font-bold text-amber-600">
                {searchFilteredCourses.filter((c) => c.status === "draft").length}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={openTrashModal}
            className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm text-left hover:border-red-300 hover:bg-red-50/50 transition-colors focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 flex items-start gap-3"
          >
            <div className="flex-shrink-0 p-2 rounded-lg bg-red-100 text-red-600">
              <TrashIcon className="h-6 w-6" />
            </div>
            <div>
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wider">Delete Bin</p>
              <p className="mt-1 text-2xl font-bold text-red-600">{trashList.length}</p>
            </div>
          </button>
        </div>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
          <h1 className="text-xl font-bold text-gray-900">Courses</h1>
          <div className="flex items-center gap-3">
            <div className="relative flex-1 sm:flex-initial sm:w-56">
              <input
                type="text"
                placeholder="Search courses"
                value={courseSearchQuery}
                onChange={(e) => setCourseSearchQuery(e.target.value)}
                className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              />
              <svg
                className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            <button
              onClick={() => navigate("/courses/newcourse")}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 whitespace-nowrap"
            >
              <PlusIcon className="h-4 w-4 mr-2" />
              Add Course
            </button>
          </div>
        </div>
        {/* Full-width course table - rows not clickable */}
        <div className="flex-1 min-h-0 overflow-auto border border-gray-200 rounded-lg bg-white shadow-sm">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50 sticky top-0 z-10">
              <tr>
                <th scope="col" className="px-3 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider w-12">
                  #
                </th>
                <th scope="col" className="px-3 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider w-20">
                  Image
                </th>
                <th scope="col" className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  Course Name
                </th>
                <th scope="col" className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  Category
                </th>
                <th scope="col" className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  Level
                </th>
                <th scope="col" className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  Status
                </th>
                <th scope="col" className="px-4 py-3 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {paginatedCourses.map((course, index) => (
                <tr key={course.id} className="hover:bg-gray-50/80 transition-colors">
                  <td className="px-3 py-3 text-sm font-medium text-gray-500 whitespace-nowrap">
                    {(currentPage - 1) * COURSES_PER_PAGE + index + 1}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap">
                    <img
                      src={
                        resolveImageUrl(course.thumbnail || course.image) ||
                        "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjQiIGhlaWdodD0iNjQiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PHJlY3Qgd2lkdGg9IjEwMCUiIGhlaWdodD0iMTAwJSIgZmlsbD0iI2YzZjRmNiIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LWZhbWlseT0iQXJpYWwsIHNhbnMtc2VyaWYiIGZvbnQtc2l6ZT0iMTIiIGZpbGw9IiM5Q0EzQTYiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuM2VtIj5ObyBJbWFnZTwvdGV4dD48L3N2Zz4="
                      }
                      alt=""
                      className="h-10 w-10 rounded-full object-cover border border-gray-200 bg-gray-100"
                      loading="lazy"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src =
                          "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjQiIGhlaWdodD0iNjQiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PHJlY3Qgd2lkdGg9IjEwMCUiIGhlaWdodD0iMTAwJSIgZmlsbD0iI2YzZjRmNiIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LWZhbWlseT0iQXJpYWwsIHNhbnMtc2VyaWYiIGZvbnQtc2l6ZT0iMTIiIGZpbGw9IiM5Q0EzQTYiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuM2VtIj5ObyBJbWFnZTwvdGV4dD48L3N2Zz4=";
                      }}
                    />
                  </td>
                  <td className="px-4 py-3 text-sm font-medium text-gray-900 max-w-[220px] truncate" title={course.title}>
                    {course.title}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600 max-w-[180px] truncate" title={formatCategories(course.categories)}>
                    {formatCategories(course.categories)}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600 capitalize whitespace-nowrap">
                    {course.level || "—"}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        const newStatus = course.status === "published" ? "draft" : "published";
                        handleStatusChange(course, newStatus);
                      }}
                      className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 ${
                        course.status === "published"
                          ? "bg-green-500"
                          : "bg-gray-300"
                      }`}
                      role="switch"
                      aria-checked={course.status === "published"}
                      title={course.status === "published" ? "Published (click to set Draft)" : "Draft (click to Publish)"}
                    >
                      <span
                        className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out mt-0.5 ${
                          course.status === "published" ? "translate-x-5" : "translate-x-0.5"
                        }`}
                      />
                    </button>
                  </td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        type="button"
                        onClick={() => {
                          if (course.status === "published") {
                            const slug = createSlug(course.title);
                            window.open(
                              `${ISYLLABI_BASE_URL}/courses/${slug}/${course.id}`,
                              "_blank"
                            );
                          } else {
                            setPreviewViewOnly(true);
                            handlePreview(course);
                          }
                        }}
                        className="p-2 text-gray-500 hover:text-indigo-600 hover:bg-indigo-50 rounded"
                        title={
                          course.status === "published"
                            ? "View on iSyllabi"
                            : "View details"
                        }
                      >
                        <EyeIcon className="h-4 w-4" />
                      </button>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/courses/${course.id}/edit`);
                          }}
                          className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded"
                          title="Edit"
                        >
                          <PencilIcon className="h-4 w-4" />
                        </button>
                      <button
                        type="button"
                        onClick={() => handleDelete(course.id)}
                        className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded"
                        title="Delete"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {searchFilteredCourses.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-4 py-12 text-center text-gray-500 text-sm">
                    No courses found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        {/* Pagination */}
        {searchFilteredCourses.length > 0 && (
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3 py-4 px-1 border-t border-gray-200 bg-white rounded-b-lg">
            <p className="text-sm text-gray-600">
              Showing{" "}
              <span className="font-medium">
                {(currentPage - 1) * COURSES_PER_PAGE + 1}
              </span>
              –
              <span className="font-medium">
                {Math.min(currentPage * COURSES_PER_PAGE, searchFilteredCourses.length)}
              </span>
              {" "}of{" "}
              <span className="font-medium">{searchFilteredCourses.length}</span>
              {" "}courses
            </p>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage <= 1}
                className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white"
              >
                Previous
              </button>
              <span className="text-sm text-gray-600 px-2">
                Page <span className="font-medium">{currentPage}</span> of{" "}
                <span className="font-medium">{totalPages}</span>
              </span>
              <button
                type="button"
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPage >= totalPages}
                className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
      {/* Modals for View / Edit */}
      {showMoreInfoModal && (
          <MoreInfoModal
            courseId={currentCourseId}
            onSubmit={handleSubmitMoreInfo}
          />
        )}
        {showContentManager && (
          <CourseContentManagerModal
            courseId={currentCourseId}
            onContentUpdate={() => {
              setShowContentManager(false);
              setCurrentCourseId(null);
              toast.success("Course setup complete!");
            }}
          />
        )}
        {showPreviewModal && previewCourse && (
          <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4 z-50">
            <div className={`bg-white w-full max-h-[90vh] shadow-2xl border border-gray-200 flex flex-col ${previewViewOnly ? "max-w-5xl rounded-2xl overflow-hidden" : "max-w-2xl rounded-lg p-8 overflow-y-auto"}`}>
              <div className={`flex justify-between items-center flex-shrink-0 ${previewViewOnly ? "px-6 py-4 border-b border-gray-200" : "mb-6"}`}>
                <h3 className={`font-bold ${previewViewOnly ? "text-xl text-gray-900" : "text-2xl text-indigo-700"}`}>
                  {previewViewOnly ? "Course details" : "Course Preview"}
                </h3>
                <button
                  onClick={handleClosePreviewModal}
                  className="text-gray-400 hover:text-gray-600 transition-colors p-1"
                  title="Close"
                >
                  <span className="sr-only">Close</span>
                  <svg
                    className="h-6 w-6"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>
              {(previewViewOnly || !editingPreview) ? (
                <>
                <div className={`space-y-6 ${previewViewOnly ? "flex-1 overflow-y-auto px-6 py-4" : ""}`}>
                  {/* Section: Overview */}
                  <section className={previewViewOnly ? "bg-gray-50 rounded-xl p-5 border border-gray-200" : ""}>
                  <div className="flex gap-6">
                    <div className="flex-shrink-0 w-48 h-48 bg-gray-100 rounded-lg overflow-hidden">
                      <img
                        src={
                          resolveImageUrl(
                            previewCourse.image || previewCourse.thumbnail
                          ) ||
                          "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZjNmNGY2Ii8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCwgc2Fucy1zZXJpZiIgZm9udC1zaXplPSIxNCIgZmlsbD0iIzY2NjY2NiIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPk5vIEltYWdlPC90ZXh0Pjwvc3ZnPg=="
                        }
                        alt={previewCourse.title}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src =
                            "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZjNmNGY2Ii8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCwgc2Fucy1zZXJpZiIgZm9udC1zaXplPSIxNCIgZmlsbD0iIzY2NjY2NiIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPk5vIEltYWdlPC90ZXh0Pjwvc3ZnPg==";
                        }}
                      />
                    </div>
                    <div className="flex-1">
                      <div className="flex justify-between items-start">
                        <h2 className="text-2xl font-bold text-gray-900">
                          {previewCourse.title}
                        </h2>
                        <span
                          className={`px-3 py-1 text-xs font-medium rounded-full ${
                            previewCourse.status === "published"
                              ? "bg-green-100 text-green-800"
                              : "bg-yellow-100 text-yellow-800"
                          }`}
                        >
                          {previewCourse.status?.toUpperCase() || "DRAFT"}
                        </span>
                      </div>

                      {previewCourse.instructor && (
                        <div className="mt-2 text-sm text-gray-600">
                          Instructor:{" "}
                          {previewCourse.instructor.name ||
                            `${previewCourse.instructor.firstName || ""} ${
                              previewCourse.instructor.lastName || ""
                            }`.trim() ||
                            "Not assigned"}
                        </div>
                      )}

                      <div className="mt-4 flex items-center gap-4 text-sm text-gray-600">
                        {previewCourse.level && (
                          <span>Level: {previewCourse.level}</span>
                        )}
                        {previewCourse.duration && (
                          <span>• Duration: {previewCourse.duration}</span>
                        )}
                        {previewCourse.price !== undefined &&
                          previewCourse.price !== null && (
                            <span>
                              • Price: $
                              {parseFloat(previewCourse.price).toFixed(2)}
                            </span>
                          )}
                      </div>
                    </div>
                  </div>
                  </section>

                  {/* Course Details in sections */}
                  <div className={`grid gap-6 mt-6 ${previewViewOnly ? "grid-cols-1 lg:grid-cols-2" : "grid-cols-1 md:grid-cols-2"}`}>
                    {/* Section: Description */}
                    <div className={previewViewOnly ? "bg-white rounded-xl p-5 border border-gray-200" : "space-y-4"}>
                      {(previewViewOnly || previewCourse.description) && (
                        <div>
                          <h3 className="text-lg font-semibold text-gray-900 mb-2 border-b border-gray-200 pb-2">
                            Description
                          </h3>
                          <div className="prose prose-sm max-w-none text-gray-900">
                            {previewCourse.description || (previewViewOnly ? "—" : null)}
                          </div>
                        </div>
                      )}

                      {(previewViewOnly || previewCourse.learning_objectives) && (
                        <div className={previewViewOnly ? "mt-4" : ""}>
                          <h3 className="text-lg font-semibold text-gray-900 mb-2 border-b border-gray-200 pb-2">
                            Learning Objectives
                          </h3>
                          <div className="prose prose-sm max-w-none text-gray-900">
                            {Array.isArray(
                              previewCourse.learning_objectives
                            ) ? (
                              <ul className="list-disc pl-5">
                                {previewCourse.learning_objectives
                                  .filter(Boolean)
                                  .map((obj, index) => (
                                    <li key={index}>{obj}</li>
                                  ))}
                              </ul>
                            ) : (
                              previewCourse.learning_objectives || (previewViewOnly ? "—" : null)
                            )}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Right Column - Prerequisites & Target Audience */}
                    <div className={previewViewOnly ? "space-y-6" : "space-y-4"}>
                      {(previewViewOnly || previewCourse.prerequisites) && (
                        <div className={previewViewOnly ? "bg-white rounded-xl p-5 border border-gray-200" : ""}>
                          <h3 className="text-lg font-semibold text-gray-900 mb-2 border-b border-gray-200 pb-2">
                            Prerequisites
                          </h3>
                          <div className="prose prose-sm max-w-none text-gray-900">
                            {Array.isArray(previewCourse.prerequisites) ? (
                              <ul className="list-disc pl-5">
                                {previewCourse.prerequisites
                                  .filter(Boolean)
                                  .map((preq, index) => (
                                    <li key={index}>{preq}</li>
                                  ))}
                              </ul>
                            ) : (
                              previewCourse.prerequisites || (previewViewOnly ? "—" : null)
                            )}
                          </div>
                        </div>
                      )}

                      {(previewViewOnly || previewCourse.target_audience) && (
                        <div className={previewViewOnly ? "bg-white rounded-xl p-5 border border-gray-200" : ""}>
                          <h3 className="text-lg font-semibold text-gray-900 mb-2 border-b border-gray-200 pb-2">
                            Target Audience
                          </h3>
                          <div className="prose prose-sm max-w-none text-gray-900">
                            {previewCourse.target_audience || (previewViewOnly ? "—" : null)}
                          </div>
                        </div>
                      )}

                      {(() => {
                        const categoriesArr = Array.isArray(
                          previewCourse.categories
                        )
                          ? previewCourse.categories
                          : (previewCourse.categories || "")
                              .split(",")
                              .map((c) => c.trim())
                              .filter(Boolean);
                        const tagsArr = Array.isArray(previewCourse.tags)
                          ? previewCourse.tags
                          : (previewCourse.tags || "")
                              .split(",")
                              .map((t) => t.trim())
                              .filter(Boolean);
                        return categoriesArr.length > 0 || tagsArr.length > 0;
                      })() && (
                        <div className={previewViewOnly ? "bg-white rounded-xl p-5 border border-gray-200 mt-6" : "flex flex-wrap gap-2 mt-4"}>
                        {previewViewOnly && <h3 className="text-lg font-semibold text-gray-900 mb-2 border-b border-gray-200 pb-2">Categories & Tags</h3>}
                        <div className={previewViewOnly ? "flex flex-wrap gap-2 mt-2" : "contents"}>
                          {(Array.isArray(previewCourse.categories)
                            ? previewCourse.categories
                            : (previewCourse.categories || "")
                                .split(",")
                                .map((c) => c.trim())
                                .filter(Boolean)
                          ).map((category, index) => (
                            <span
                              key={`cat-${index}`}
                              className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                            >
                              {category}
                            </span>
                          ))}
                          {(Array.isArray(previewCourse.tags)
                            ? previewCourse.tags
                            : (previewCourse.tags || "")
                                .split(",")
                                .map((t) => t.trim())
                                .filter(Boolean)
                          ).map((tag, index) => (
                            <span
                              key={`tag-${index}`}
                              className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800"
                            >
                              {tag}
                            </span>
                          ))}
                        </div>
                        </div>
                      )}
                    </div>
                  </div>
                  {!previewViewOnly && (
                    <div className="flex justify-end mt-6">
                      <button
                        onClick={handleEditPreview}
                        className="px-6 py-2 text-sm font-semibold text-white bg-indigo-600 border border-transparent rounded-md shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors"
                      >
                        Edit
                      </button>
                    </div>
                  )}
                </div>
                {previewViewOnly && (
                  <div className="flex-shrink-0 px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-2xl flex justify-end">
                    <button
                      type="button"
                      onClick={handleClosePreviewModal}
                      className="px-5 py-2.5 text-sm font-medium text-gray-900 bg-white border border-gray-300 rounded-lg shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                    >
                      Close
                    </button>
                  </div>
                )}
                </>
              ) : (
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                  }}
                  className="space-y-4"
                >
                  {/* Main Info Fields */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Title
                    </label>
                    <input
                      type="text"
                      name="title"
                      value={previewForm.title}
                      onChange={handlePreviewFormChange}
                      className="mt-1 block w-full border rounded p-2"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Description
                    </label>
                    <textarea
                      name="description"
                      value={previewForm.description}
                      onChange={handlePreviewFormChange}
                      className="mt-1 block w-full border rounded p-2"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Level
                    </label>
                    <select
                      name="level"
                      value={previewForm.level}
                      onChange={handlePreviewFormChange}
                      className="mt-1 block w-full border rounded p-2"
                    >
                      <option value="beginner">Beginner</option>
                      <option value="intermediate">Intermediate</option>
                      <option value="advanced">Advanced</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Duration
                    </label>
                    <input
                      type="number"
                      name="duration"
                      value={previewForm.duration}
                      onChange={handlePreviewFormChange}
                      className="mt-1 block w-full border rounded p-2"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Price
                    </label>
                    <input
                      type="number"
                      name="price"
                      value={previewForm.price}
                      onChange={handlePreviewFormChange}
                      className="mt-1 block w-full border rounded p-2"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Status
                    </label>
                    <select
                      name="status"
                      value={previewForm.status}
                      onChange={handlePreviewFormChange}
                      className="mt-1 block w-full border rounded p-2"
                    >
                      <option value="draft">Draft</option>
                      <option value="published">Published</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Instructor
                    </label>
                    <select
                      name="instructorId"
                      value={previewForm.instructorId}
                      onChange={handlePreviewFormChange}
                      className="mt-1 block w-full border rounded p-2"
                    >
                      {instructors.map((inst) => (
                        <option key={inst.id} value={inst.id}>
                          {inst.firstName} {inst.lastName} ({inst.email})
                        </option>
                      ))}
                    </select>
                  </div>
                  {/* More Info Fields Inline */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Prerequisites
                    </label>
                    <textarea
                      name="prerequisites"
                      value={previewMoreInfo.prerequisites}
                      onChange={(e) =>
                        setPreviewMoreInfo((prev) => ({
                          ...prev,
                          prerequisites: e.target.value,
                        }))
                      }
                      className="mt-1 block w-full border rounded p-2"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Learning Objectives
                    </label>
                    <textarea
                      name="learningObjectives"
                      value={previewMoreInfo.learningObjectives}
                      onChange={(e) =>
                        setPreviewMoreInfo((prev) => ({
                          ...prev,
                          learningObjectives: e.target.value,
                        }))
                      }
                      className="mt-1 block w-full border rounded p-2"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Target Audience
                    </label>
                    <textarea
                      name="targetAudience"
                      value={previewMoreInfo.targetAudience}
                      onChange={(e) =>
                        setPreviewMoreInfo((prev) => ({
                          ...prev,
                          targetAudience: e.target.value,
                        }))
                      }
                      className="mt-1 block w-full border rounded p-2"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Category
                    </label>
                    <input
                      type="text"
                      name="categories"
                      value={previewMoreInfo.categories}
                      onChange={(e) =>
                        setPreviewMoreInfo((prev) => ({
                          ...prev,
                          categories: e.target.value,
                        }))
                      }
                      className="mt-1 block w-full border rounded p-2"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Tags (comma separated)
                    </label>
                    <input
                      type="text"
                      name="tags"
                      value={previewMoreInfo.tags}
                      onChange={(e) =>
                        setPreviewMoreInfo((prev) => ({
                          ...prev,
                          tags: e.target.value,
                        }))
                      }
                      className="mt-1 block w-full border rounded p-2"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Thumbnail
                    </label>
                    <input
                      type="file"
                      name="thumbnail"
                      onChange={(e) =>
                        setPreviewMoreInfo((prev) => ({
                          ...prev,
                          thumbnail: e.target.files[0],
                          thumbnailUrl: e.target.files[0]
                            ? URL.createObjectURL(e.target.files[0])
                            : prev.thumbnailUrl,
                        }))
                      }
                      className="mt-1 block w-full border rounded p-2"
                    />
                    {previewMoreInfo.thumbnailUrl &&
                      !previewMoreInfo.thumbnailUrl.includes(
                        "/api/upload/image"
                      ) && (
                        <img
                          src={
                            previewMoreInfo.thumbnailUrl.startsWith("blob:")
                              ? previewMoreInfo.thumbnailUrl
                              : resolveImageUrl(previewMoreInfo.thumbnailUrl)
                          }
                          alt="Thumbnail"
                          className="h-32 mt-2 rounded shadow border object-contain"
                        />
                      )}
                  </div>
                  <div className="flex justify-end mt-6">
                    <button
                      type="button"
                      onClick={() => setEditingPreview(false)}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 mr-2"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      onClick={() => handleSavePreview(previewMoreInfo)}
                      className="px-6 py-2 text-sm font-semibold text-white bg-indigo-600 border border-transparent rounded-md shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors"
                    >
                      Save
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        )}
      {/* Delete Bin modal */}
      {showTrashModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg w-full max-w-2xl max-h-[85vh] overflow-hidden shadow-xl border border-gray-200 flex flex-col">
            <div className="flex justify-between items-center p-4 border-b border-gray-200">
              <h3 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                <TrashIcon className="h-6 w-6 text-red-500" />
                Delete Bin
              </h3>
              <button
                type="button"
                onClick={() => setShowTrashModal(false)}
                className="p-2 text-gray-500 hover:text-gray-700 rounded hover:bg-gray-100"
                title="Close"
              >
                <span className="sr-only">Close</span>
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-4">
              {trashLoading ? (
                <div className="flex justify-center py-12">
                  <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-600" />
                </div>
              ) : trashList.length === 0 ? (
                <p className="text-gray-500 text-center py-8">No deleted courses.</p>
              ) : (
                <ul className="space-y-2">
                  {trashList.map((course) => (
                    <li
                      key={course.id}
                      className="flex items-center justify-between gap-4 p-3 bg-gray-50 rounded-lg border border-gray-200"
                    >
                      <div className="min-w-0 flex-1">
                        <p className="font-medium text-gray-900 truncate">{course.title}</p>
                        <p className="text-xs text-gray-500 mt-0.5">
                          {course.level || "—"} • Deleted
                        </p>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <button
                          type="button"
                          onClick={() => handleRestore(course.id)}
                          className="p-2 text-gray-500 hover:text-green-600 hover:bg-green-50 rounded"
                          title="Restore"
                        >
                          <ArrowUturnLeftIcon className="h-5 w-5" />
                        </button>
                        <button
                          type="button"
                          onClick={() => handlePermanentDelete(course.id)}
                          className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded"
                          title="Permanent delete"
                        >
                          <TrashIcon className="h-5 w-5" />
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Courses;
